/*
 * @description:
 * @author: Jason Wong
 * @since: 2021-12-23 13:20:16
 */

import {searchEntries} from "@/api/Termbase";

const CAT_FLOW_TEXTS = ["翻译", "审校", "通读"];
const CAT_AI_ASSISTANT_WARNING = [
    ...CAT_FLOW_TEXTS.map((stage) => `${stage}人员正在${stage}此句段，无法使用AI助手。`),
    "任务已完成，无法使用AI助手。",
];
/**
 * detail stage of sentence
 */
const TU_STATUS = {
    //extra
    /**
     * expty is composite status,
     * whose status is translating and was never edited by anyone.
     */
    //empty: 9,

    translating: 0,
    translated: 1,
    reviewing: 2,
    rejectedByReviewer: 3,
    reviewPassed: 4,
    proofreading: 5,
    rejectedByProofreader: 6,
    proofreadPassed: 7,
    //commentByTeacher: 8,
};

const TU_STATUS_TEXTS = [
    "翻译中",
    "翻译已确认",
    "审校中",
    "审校已拒绝",
    "审校已确认",
    "通读中",
    "通读已拒绝",
    "通读已确认",
    "教师已点评",
    "未翻译",
];

/**
 * get description of detail stage of sentence
 * @param {Number} detailStage
 */
function getStageDetailText(detailStage) {
    return TU_STATUS_TEXTS[detailStage];
}

const TRANSLATE_EDITABLE_TU_STATUS = {
    translating: 0,
    rejectedByReviewer: 3,
};

const TRANSLATE_CONFIRMABLE_TU_STATUS = TRANSLATE_EDITABLE_TU_STATUS;

const REVIEW_EDITABLE_TU_STATUS = {
    translated: 1,
    reviewing: 2,
};

const PROOFREAD_EDITABLE_TU_STATUS = {
    reviewPassed: 4,
    proofreading: 5,
};

const TRANSLATE_READONLY_TU_STATUS = {
    translated: 1,
    reviewing: 2,
    reviewPassed: 4,
    proofreading: 5,
    rejectedByProofreader: 6,
    proofreadPassed: 7,
};

const REVIEW_READONLY_TU_STATUS = {
    translating: 0,
    reviewPassed: 4,
    rejectedByReviewer: 3,
    proofreading: 5,
    proofreadPassed: 7,
    rejectedByProofreader: 6,
};

const PROOFREAD_READONLY_TU_STATUS = {
    translating: 0,
    translated: 1,
    reviewing: 2,
    rejectedByReviewer: 3,
    proofreadPassed: 7,
    rejectedByProofreader: 6,
};

const TRANSLATE_CAT_DISABLED_TU_STATUS = {
    reviewing: 2,
    reviewPassed: 4,
    proofreading: 5,
    proofreadPassed: 7,
    rejectedByProofreader: 6,
};

const REVIEW_CAT_DISABLED_TU_STATUS = {
    translating: 0,
    proofreading: 5,
    proofreadPassed: 7,
    rejectedByProofreader: 6,
};

const PROOFREAD_CAT_DISABLED_TU_STATUS = {
    translating: 0,
    translated: 1,
    reviewing: 2,
    rejectedByReviewer: 3,
    proofreadPassed: 7,
};

const EDITOR_MODE = {
    translate: 0,
    review: 1,
    proofread: 2,
    readonly: 3,
};

const TARGET_SOURCE = {
    manual: 0, // 手动输入
    autofillByTM: 1, // TM 填充
    autofillByMT: 2, // 机翻填充
    reject: 3, // 句段被拒绝
    preTM: 4, //记忆库填充(预翻译)
    srcEdit: 5, //编辑原文
    CombineSrc: 6, //合并句段
    SplitSrc: 7, //拆分句段
};

const REPLACEMENT_SOURCE2TARGET_SOURCE = {
    tm: TARGET_SOURCE.autofillByTM,
    mt: TARGET_SOURCE.autofillByMT,
};

/**
 * 句段状态图标右侧旁边的 TM 匹配率的状态
 */
const MATCH_SCORE_STATUS = {
    idle: 0, // 用户没有引用 TM 匹配，手动编辑
    asIs: 1, // 用户直接引用 TM 匹配，没有继续编辑
    asIsExact: 2, // 用户直接引用 TM 100% 匹配，没有继续编辑
    asIsCM: 3, // 用户直接引用 TM 上下文匹配(context match)，没有继续编辑
    edited: 4, // 用户引用了 TM 匹配，并且继续编辑
};

const PRETRANSLATE_BY = {
    none: 0,
    tm: 1,
    mt: 2,
    both: 3,
};

const ASSET_SOURCE = {
    tm: 0,
    mt: 1,
    tb: 2,
};
const ASSET_SOURCE_TEXT = {
    [ASSET_SOURCE.tm]: "记忆库",
    [ASSET_SOURCE.mt]: "机器翻译",
    [ASSET_SOURCE.tb]: "术语库",
};

const QA_LEVEL = {
    high: 2,
    normal: 1,
    slight: 0,
};
const QA_LEVEL_TEXT = {
    high: "严重",
    normal: "一般",
    slight: "轻微",
};

const FONT_SIZE_MAP = {
    little: 12,
    medium: 16,
    big: 24,
    large: 30,
};

import { copyFields } from "../../../utils/dataUtil";
import { insertNodeAfter, isTextNode } from "../../../utils/domHelper";
import { isBlank, stringUti } from "../../../utils/String";

/**
 * Asset source(TM,MT and TB) loader
 */
class AssetSourceLoader {
    constructor() {
        this._tmFetchSource = null;
        this._tmFetchForm = {
            projectId: null,
            sentenceId: null,
        };
        this._mtFetchForm = {
            mtId: null,
            srcLang: null,
            tgtLang: null,
            srcTextList: null,
        };
        this._tbFetchForm = {
            glossaryIds: null,
            writableTBId: null,
            srcLang: null,
            srcText: null,
            tgtLang: null,
        };
        this._gptConversationFetchForm = {
            sessionId: null,
        };

        this._tmFetchCancel = null;
        this._mtFetchCancel = null;
        this._tbFetchCancel = null;
        this._gptConversationFetchCancel = null;

        this._fetchKey = 0;
        this._dataFetched = null;

        this._loadersContext = {
            tm: {
                enable: false,
                disabledMessage: "未设置记忆库",
                running: false,
            },
            mt: {
                enable: false,
                disabledMessage: "未设置机器翻译",
                running: false,
                hasError: false,
                errorMessage: null,
            },
            tb: {
                enable: false,
                disabledMessage: "未设置术语库",
                running: false,
                writableTBId: null,
            },
            gptConversation: {
                enable: true, // 目前设置为一直启用
                disabledMessage: "未设置 chat 模型",
                running: false,
            },
        };
    }

    get loadersContext() {
        return this._loadersContext;
    }

    _updateLoadersContextInfo() {
        this._loadersContext.tm.enable = !!this._tmFetchForm.projectId;
        this._loadersContext.mt.enable = !isBlank(this._mtFetchForm.mtId);
        this._loadersContext.tb.enable = !!this._tbFetchForm.glossaryIds && this._tbFetchForm.glossaryIds.length > 0;
        this._loadersContext.tb.writableTBId = this._tbFetchForm.writableTBId;
    }

    whenDataFetched(func) {
        this._dataFetched = func;
    }

    updateFetchForm(queryFormMap) {
        if (!queryFormMap) return;

        for (const name in queryFormMap) {
            const assocForm = this[`_${name}FetchForm`];
            if (!assocForm) continue;

            for (const attr in queryFormMap[name]) {
                queryFormMap[name][attr] != null && (assocForm[attr] = queryFormMap[name][attr]);
            }
        }
        this._updateLoadersContextInfo();
    }

    _updateFetchKey() {
        this._fetchKey = this._fetchKey >= Number.MAX_SAFE_INTEGER ? 0 : this._fetchKey + 1;
    }

    cancel() {
        this._updateFetchKey();
        this._tmFetchCancel && this._tmFetchCancel();
        this._mtFetchCancel && this._mtFetchCancel();
        this._tbFetchCancel && this._tbFetchCancel();
        this._gptConversationFetchCancel && this._gptConversationFetchCancel();
    }

    _closeLoaderRunningState(loaderName, fetchKey) {
        if (fetchKey === this._fetchKey) {
            this._loadersContext[loaderName].running = false;
        }
    }

    fetch(queryFormMap) {
        console.log("#-# data reciver to fetch");
        this._updateFetchKey();
        if (!queryFormMap) return;

        //cancel query
        const currentFetchKey = this._fetchKey;
        this._tmFetchCancel && this._tmFetchCancel();
        this._mtFetchCancel && this._mtFetchCancel();
        this._tbFetchCancel && this._tbFetchCancel();
        this._gptConversationFetchCancel && this._gptConversationFetchCancel();
        this.updateFetchForm(queryFormMap);

        if (this._loadersContext.tm.enable) {
            this._loadersContext.tm.running = true;
            const { promise, cancel } = queryTMEntryMatch(this._tmFetchForm);
            this._tmFetchCancel = cancel;
            promise
                .then((res) => {
                    if (currentFetchKey === this._fetchKey && this._dataFetched) {
                        this._dataFetched({ source: "tm", res });
                    }
                })
                .catch((err) => {
                    console.log("error exists when fetch tm:%s", err);
                })
                .then((res) => {
                    this._closeLoaderRunningState("tm", currentFetchKey);
                });
        }

        if (this._loadersContext.mt.enable) {
            this._loadersContext.mt.running = true;
            this._loadersContext.mt.hasError = false;
            this._loadersContext.mt.errorMessage = null;
            const { promise, cancel } = matchMT(this._mtFetchForm);
            this._mtFetchCancel = cancel;
            promise
                .then((res) => {
                    if (this._dataFetched && currentFetchKey === this._fetchKey) {
                        this._dataFetched({ source: "mt", res });
                    }
                })
                .catch((err) => {
                    console.log("error exists when fetch mt:%s", err);
                    if (!!err && !!err.code && err.code === "ERR_CANCELED") return;
                    this._loadersContext.mt.hasError = true;
                    this._loadersContext.mt.errorMessage = err;
                })
                .then((res) => {
                    this._closeLoaderRunningState("mt", currentFetchKey);
                });
        }

        if (this._loadersContext.tb.enable) {
            this._loadersContext.tb.running = true;
            const { promise, cancel } = searchEntries(this._tbFetchForm);
            this._tbFetchCancel = cancel;
            promise
                .then((res) => {
                    if (this._dataFetched && currentFetchKey === this._fetchKey) {
                        this._dataFetched({ source: "tb", res });
                    }
                })
                .catch((err) => {
                    console.log("error exists when fetch tb:%s", err);
                })
                .then((res) => {
                    this._closeLoaderRunningState("tb", currentFetchKey);
                });
        }

        if (this._loadersContext.gptConversation.enable) {
            this._loadersContext.gptConversation.running = true;
            const { promise, cancel } = getGptConversations(this._gptConversationFetchForm);
            this._gptConversationFetchCancel = cancel;
            promise
                .then((res) => {
                    if (this._dataFetched && currentFetchKey === this._fetchKey) {
                        this._dataFetched({ source: "gptConversation", res });
                    }
                })
                .catch((err) => {
                    console.log("error exists when fetch gptConversation:%s", err);
                })
                .then((res) => {
                    this._closeLoaderRunningState("gptConversation", currentFetchKey);
                });
        }
    }
}

const TERM_START_NODE_NAME = "tms";
const TERM_START_NODE_NAME_UPPER = "TMS";
const TERM_END_NODE_NAME = "tme";
const TERM_END_NODE_NAME_UPPER = "TME";
const TERM_MIDDLE_NODE_NAME = "tmm";
const TERM_MIDDLE_NODE_NAME_UPPER = "TMM";
const SYMBOL_TAG_MAP = {
    _s: TERM_START_NODE_NAME,
    _m: TERM_MIDDLE_NODE_NAME,
    _e: TERM_END_NODE_NAME,
};

/**
 * Text segment stands for a virtual node.
 * A segment is a wrapper of lowest level node(a text node)
 * but record top level node and offset.
 */
class TextSegment {
    constructor(textNode) {
        this.textNode = textNode;
        this.rootEl = null;
        this.symbols = {};
        this.start = 0;
        this.end = 0;
    }

    get length() {
        return this.end - this.start + 1;
    }

    containsSymbol(...symbols) {
        for (const symbol of symbols || []) {
            if (!this.symbols[symbol]) return false;
        }
        return true;
    }
}

/**
 * create a text segment
 *
 * @param {Element} textNode
 * @param {Number} start
 * @param {Number} wrapper
 * @returns
 */
function createTextSegment(textNode, start, wrapper) {
    let segment = new TextSegment(textNode);
    let curNode = textNode.parentElement;
    segment.rootEl = textNode;
    while (curNode != wrapper) {
        if (parent.tagName == TERM_MIDDLE_NODE_NAME_UPPER) {
            segment.symbols._m = true;
        } else if (parent.tagName == TERM_START_NODE_NAME_UPPER) {
            segment.symbols._s = true;
        } else if (parent.tagName == TERM_END_NODE_NAME_UPPER) {
            segment.symbols._e = true;
        }
        segment.rootEl = curNode;
        curNode = curNode.parentElement;
    }
    segment.start = start;
    segment.end = start + textNode.textContent.length - 1;
    return segment;
}

/**
 *
 * @param {Array<TextSegment>} segments
 * @param {Number} pos
 */
function findSegmentWithPos(segments, pos, searchStartPos) {
    for (let i = searchStartPos == null ? 0 : searchStartPos; i < segments.length; i++) {
        const segment = segments[i];
        if (segment.start <= pos && segment.end >= pos) {
            return [segment, i];
        }
    }

    return [null, -1];
}

/**
 *
 * @param {Array<TextSegment>} segments
 * @param {Number} start
 * @param {Number} end
 */
function findFirstSegmentsWithRange(segments, start, end, searchStartPos) {
    let curSegment;
    for (let i = searchStartPos; i < segments.length; i++) {
        curSegment = segments[i];
        if (
            (curSegment.start >= start && curSegment.start <= end) ||
            (curSegment.end >= start && curSegment.end <= end)
        ) {
            return i;
        }

        if (curSegment.start > end) break;
    }
    return -1;
}

/**
 *
 * Copy a text segment and add extra symbols into it.
 *
 * @param {TextSegment} segment
 * @param {Number} start
 * @param {number} end
 */
function copySegment(segment, start, end, extraSymbols) {
    let newTextNode = document.createTextNode(
        segment.textNode.textContent.substring(start - segment.start, end - segment.start + 1)
    );
    let newRootEl = newTextNode;
    let newSymbol = {};
    let sourceEl = segment.textNode;
    while (sourceEl != segment.rootEl) {
        let parent = document.createElement(sourceEl.parentElement.tagName);
        parent.appendChild(newRootEl);
        newRootEl = parent;
        if (parent.tagName === TERM_START_NODE_NAME_UPPER) {
            newSymbol._s = true;
        } else if (parent.tagName === TERM_END_NODE_NAME_UPPER) {
            newSymbol._e = true;
        } else if (parent.tagName === TERM_MIDDLE_NODE_NAME_UPPER) {
            newSymbol._m = true;
        }
        sourceEl = sourceEl.parentElement;
    }

    let newSegment = new TextSegment(newTextNode);
    newSegment.start = start;
    newSegment.end = end;
    newSegment.rootEl = newRootEl;
    newSegment.symbols = newSymbol;
    if (extraSymbols != null && extraSymbols.length > 0) {
        let curSymbol;
        for (let i = extraSymbols.length - 1; i > -1; i--) {
            curSymbol = extraSymbols[i];
            if (!newSegment.symbols[curSymbol]) {
                let newWrapper = document.createElement(SYMBOL_TAG_MAP[curSymbol]);
                newWrapper.appendChild(newSegment.rootEl);
                newSegment.rootEl = newWrapper;
                newSegment.symbols[curSymbol] = true;
            }
        }
    }

    return newSegment;
}

/**
 *
 * Split a text segment into more segments
 *
 * @param {TextSegment} segment
 * @param {Number} start
 * @param {Number} end
 */
function splitSegment(segment, start, end, extraSymbols) {
    if (segment.start == start && segment.end == end && segment.containsSymbol(extraSymbols)) {
        return { splitedSegments: [segment], segmentbewttenPos: 0 };
    }

    let splitedSegments = [];
    let segmentbewttenPos = 0;
    if (segment.start < start) {
        splitedSegments.push(copySegment(segment, segment.start, start - 1));
        segmentbewttenPos = 1;
    }
    splitedSegments.push(copySegment(segment, start, end, extraSymbols));
    if (segment.end > end) {
        splitedSegments.push(copySegment(segment, end + 1, segment.end));
    }

    return {
        splitedSegments,
        segmentbewttenPos,
    };
}

/**
 *
 * Format src of sentence with term list.
 *
 * @param {String} src
 * @param {Array{start:Number,end:Number}} tbList
 * @returns
 */
function formatSrcWithTBList(src, tbList) {
    if (isBlank(src) || !tbList || tbList.length < 1) return src;
    console.log("#-# format src with tb list", src);
    let rootNodeText = document.createTextNode(src);
    let srcEl = document.createElement("div");
    srcEl.appendChild(rootNodeText);

    let textSegments = [createTextSegment(srcEl.childNodes[0], 0, srcEl)];
    for (const tb of tbList) {
        for (const tbRange of tb.pos || []) {
            if (tbRange.end - tbRange.start < 0) continue;

            const [segment, index] = findSegmentWithPos(textSegments, tbRange.start);
            if (index == -1) break;

            if (tbRange.end - tbRange.start == 0) {
                let { splitedSegments, pos } = splitSegment(segment, tbRange.start, tbRange.start, ["_s", "_e", "_m"]);
                if (splitedSegments[pos] !== segment) {
                    textSegments.splice(index, 1, ...splitedSegments);
                    insertNodeAfter(segment.rootEl, ...splitedSegments.map((s) => s.rootEl));
                    segment.rootEl.remove();
                    index = index + pos;
                }
            } else {
                let splitResult, splitedSegments, pos;
                splitResult = splitSegment(segment, tbRange.start, tbRange.start, ["_s", "_m"]);
                splitedSegments = splitResult.splitedSegments;
                pos = splitResult.segmentbewttenPos;
                if (splitedSegments[pos] !== segment) {
                    textSegments.splice(index, 1, ...splitedSegments);
                    insertNodeAfter(segment.rootEl, ...splitedSegments.map((s) => s.rootEl));
                    segment.rootEl.remove();
                    index = index + pos;
                }

                if (tbRange.end - tbRange.start >= 2) {
                    let segmentAtRangeIndex = null;
                    while (
                        (segmentAtRangeIndex = findFirstSegmentsWithRange(
                            textSegments,
                            tbRange.start + 1,
                            tbRange.end - 1,
                            index + 1
                        )) != -1
                    ) {
                        const segmentAtRange = textSegments[segmentAtRangeIndex];
                        splitResult = splitSegment(
                            segmentAtRange,
                            Math.max(tbRange.start + 1, segmentAtRange.start),
                            Math.min(tbRange.end - 1, segmentAtRange.end),
                            ["_m"]
                        );
                        splitedSegments = splitResult.splitedSegments;
                        pos = splitResult.segmentbewttenPos;
                        if (splitedSegments[pos] !== segmentAtRange) {
                            textSegments.splice(segmentAtRangeIndex, 1, ...splitedSegments);
                            insertNodeAfter(segmentAtRange.rootEl, ...splitedSegments.map((s) => s.rootEl));
                            segmentAtRange.rootEl.remove();
                        }
                        if (pos > 0) index++;
                        index++;
                    }
                }

                [segment, index] = findSegmentWithPos(textSegments, tbRange.end, index + 1);
                splitResult = splitSegment(segment, tbRange.end, tbRange.end, ["_e", "_m"]);
                splitedSegments = splitResult.splitedSegments;
                pos = splitResult.segmentbewttenPos;
                if (splitedSegments[pos] !== segment) {
                    textSegments.splice(index, 1, ...splitedSegments);
                    insertNodeAfter(segment.rootEl, ...splitedSegments.map((s) => s.rootEl));
                    segment.rootEl.remove();
                    index = index + pos;
                }
            }
        }
    }

    return srcEl.innerHTML;
}

const SRC_CELL_CLASS_NAME = "sentence-cell__src";
const TGT_CELL_CLASS_NAME = TRACK_EDITOR_CLASS_NAME;
/**
 *
 * @param {Element} fragment
 */
function removeSegSymbol(fragment) {
    console.log("#-# removeSegSymbol");
    if (!fragment.childNodes || fragment.childNodes.length < 1) return fragment;
    for (let i = 0; i < fragment.childNodes.length; i++) {
        const curEl = fragment.childNodes[i];
        if (isSegSymbol(curEl)) {
            curEl.remove();
            i--;
        } else {
            removeSegSymbol(curEl);
        }
    }
    return fragment;
}

function getSectionText() {
    const section = window.getSelection();
    if (!section || section.rangeCount < 1) return;
    const range = section.getRangeAt(0);
    if (range.collapsed) return;

    let curEl = range.commonAncestorContainer;
    while (curEl) {
        if (
            curEl.classList &&
            (curEl.classList.contains(SRC_CELL_CLASS_NAME) || curEl.classList.contains(TGT_CELL_CLASS_NAME))
        ) {
            const text = removeSegSymbol(range.cloneContents()).textContent;
            const source = curEl.classList.contains(SRC_CELL_CLASS_NAME) ? "srcText" : "tgtText";
            return { text, source };
        }
        curEl = curEl.parentElement;
    }

    return null;
}

import { getTextOfHTMLString } from "../../../utils/domHelper";
import { uuid } from "../../../utils/uuid";

const MAX_LENGTH_OF_SRC_TEXT = 20000;
const MAX_LENGTH_OF_TGT_TEXT = 20000;
/**
 *
 * @param {Array<Object>} sentences
 */
function generateSaveSentence(sentence) {
    let newSentence = copyFields(sentence, [
        "id",
        "fileId",
        "srcText",
        "srcWithFormat",
        "tgtSource",
        "tgtText",
        "tgtPureText",
        "matchingDegree",
        "tmId",
        "tuId",
        "isEdit",
        "postils",
    ]);
    sentence.tgtText = newSentence.tgtText = sentence.html;
    sentence.tgtPureText = newSentence.tgtPureText = getTextOfHTMLString(
        sentence.html,
        (node) => node.tagName && node.tagName.toLocaleLowerCase() === "del"
    );
    return newSentence;
}

function generateTranslateSentence(sentence) {
    let newSentence = copyFields(sentence, ["id"]);
    return newSentence;
}

/**
 *
 * @param {Object} sentence
 * @param {Object} response
 */
function updateSentenceInfoWithResponse(sentence, response) {
    for (const attr in response) {
        attr in sentence && (sentence[attr] = response[attr]);
    }
}

/**
 *
 * @param {String} projectId
 * @param {Number} taskType
 * @param {String} taskItemId
 * @param {Object} sentence
 */
function wrapperSentenceForQaCheck(projectId, taskType, taskItemId, sentence) {
    return {
        projectId,
        taskType,
        taskItemId,
        sentences: [
            {
                id: sentence.id,
                srcText: sentence.srcText,
                tgtText: sentence.tgtPureText,
                tgtPureText: sentence.tgtPureText,
            },
        ],
    };
}

/**
 * map of sentence and qa
 */
class QaSentenceMap {
    constructor() {
        this._qaList = [];
        this._sentencePosition = [];
    }

    init(sentenceList) {
        if (!sentenceList || sentenceList.length < 1) return;
        let qaList = [];
        for (const sentence of sentenceList) {
            const sentenceStart = qaList.length;
            for (const issue of sentence.issues || []) {
                issue.tgtText = sentence.tgtPureText;
                qaList.push(issue);
            }
            this._sentencePosition.push({
                id: sentence.id,
                start: sentenceStart,
                end: qaList.length,
            });
        }
        this._qaList = qaList;
    }

    get qaList() {
        return this._qaList;
    }

    update(sentence, issues) {
        if (!sentence) return;
        const sentenceId = sentence.id;
        console.log("#-# QaSentenceMap");

        const pos = this._sentencePosition.$$index((sp) => sp.id === sentenceId);
        if (pos < 0) return;

        let tempPosInfo = this._sentencePosition[pos];
        const updatingIssures = issues || [];
        //updatingIssures.forEach((i) => (i.tgtText = sentence.tgtPureText));
        const offset = updatingIssures.length + tempPosInfo.start - tempPosInfo.end;
        this._qaList.splice(tempPosInfo.start, tempPosInfo.end - tempPosInfo.start, ...updatingIssures);
        if (offset != 0) {
            tempPosInfo.end += offset;
            for (let i = pos + 1; i < this._sentencePosition.length; i++) {
                tempPosInfo = this._sentencePosition[i];
                tempPosInfo.start += offset;
                tempPosInfo.end += offset;
            }
        }
        console.log("#-# res", this._qaList);
    }
}

/**
 *
 * @param {Array<any>} sentences
 */
function getQaCheckableSentences(sentences, taskType) {
    console.log("#-# getQaCheckableSentences");
    if (!sentences || sentences.length < 1) {
        return [];
    }
    return sentences.filter((s) => s.currentStage === taskType);
}

/**
 *
 * @param {Object} sentence
 * @param {{id:String,issueCount:Number,issueLevel:Number}} data
 */
function updateSentenceQaInfo(sentence, data) {
    console.log("#-# updateSentenceQaInfo", sentence, data);
    sentence.issueCount = data.issueCount;
    sentence.issueLevel = data.issueLevel;
}

class SimpleActionSource {
    constructor() {
        this._iscanceled = false;
    }

    get isCanceled() {
        return this._iscanceled;
    }

    cancel() {
        this._iscanceled = true;
    }
}

/**
 * get src text from segment
 * @param {{srcText:String}} segment
 * @returns
 */
function srcTextExtractor(segment) {
    return segment.srcText;
}

/**
 *
 * @param {{srcText:String,html:String}} segment
 * @returns
 */
function tgtTextExtractor(segment) {
    return isBlank(segment.html)
        ? segment.srcText
        : getTextOfHTMLString(segment.html, (node) => node.tagName && node.tagName.toLocaleLowerCase() === "del");
}

const SEGMENT_RANGE_CHARS = ">>||<<";
function generateSegmentRangeId(fileId, startSentenceNumber, endSentenceNumber) {
    return `${fileId}${SEGMENT_RANGE_CHARS}${startSentenceNumber}${SEGMENT_RANGE_CHARS}${endSentenceNumber}`;
}

/**
 * A lock needed when user want to edit setence.
 * Lock's state should be one of following four states:
 * 1) none:don't own the lock;
 * 2) required
 * 3) requireing;
 * 4) releasing;
 */
const SENTENCE_LOCK_STATE = {
    none: 0,
    requiring: 10,
    releasing: 20,
    owned: 30,
};

const SENTENCE_UPDATING_STATE = {
    detach: 0,
    ready: 1,
    updating: 2,
};

const CAT_WS_RESPONSE_CODE = {
    taskOwnedByOther: 20001,
};

const CAT_WS_STATE = {
    notConnected: 0,
    connecting: 1,
    connected: 2,
};

function createVirtualTaskItemForPM(fileId, fileName) {
    return {
        id: uuid(),
        fileId,
        fileName,
        /**
         * Give a never reachable taskType for this virtual task item,
         * which means every sentence's state is less then it.So every
         * sentence is readonly.
         */
        taskType: Number.MAX_SAFE_INTEGER,
    };
}

const FONT_STYLE_TEMPLATE = `
  .$classId$ .sentence-cell__src,
  .$classId$ .sentence-cell__tgt .ve-editor
  {
      font-size:$fontSize$px !important;
      line-height:1.7142;
  }

  .$classId$ .sentence-cell__src .seg.seg-text,
  .$classId$ .sentence-cell__tgt .ve-editor .seg.seg-text[style] {
    font-size:$fontSize$px !important;
  }
`;
function setEditFontSize(editorClassId, fontSize) {
    console.log("#-# setEditFontSize");
    let styleEl = document.querySelector(`style#${editorClassId}`);
    if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = editorClassId;
        document.body.append(styleEl);
    }
    styleEl.innerHTML = FONT_STYLE_TEMPLATE.replace("$classId$", editorClassId)
        .replace("$classId$", editorClassId)
        .replace("$fontSize$", fontSize);

    return function () {
        styleEl.remove();
    };
}

function flatRange(start, end, gap = 1) {
    let res = [];
    for (let i = start; i <= end; i += gap) {
        res.push(i);
    }
    return res;
}
class FontSizeScaler {
    constructor() {
        this._fonts = [...flatRange(12, 28, 2), 36, 40, 48, 72];
        this._pos = 0;
        this._cb = null;
    }

    get canNext() {
        return this._pos < this._fonts.length - 1;
    }

    get canScaleBack() {
        return this._pos > 0;
    }

    next() {
        if (this.canNext) {
            this._pos++;
            !!this._cb && this._cb(this._fonts[this._pos]);
        }
    }

    back() {
        if (this.canScaleBack) {
            this._pos--;
            !!this._cb && this._cb(this._fonts[this._pos]);
        }
    }

    whenUpdated(cb) {
        console.log('#callback',this);
        this._cb = cb;
    }
}

import Diff from "../../../utils/diff";
import domHelper from "@/components/trackEditor/util/dom";
import { TRACK_EDITOR_CLASS_NAME } from "@/components/trackEditor/data/consts";
import { isSegSymbol } from "@/components/trackEditor/service/action";
import {queryTMEntryMatch} from "@/api/editor";
import {getGptConversations} from "@/api/GPT";
import {matchMT} from "@/api/MT";
const DIFF = new Diff();
function makeupCombineContext(sentences, projectId, fileId) {
    let res = {
        projectId,
        fileId,
        newSec: "",
        newTgt: "",
        sentenceList: [],
    };

    for (let i = 0; i < sentences.length; i++) {
        const curSentence = sentences[i];
        res.newSec += curSentence.srcWithFormat;
        if (!isBlank(curSentence.html)) {
            res.newTgt += curSentence.html;
        }

        res.sentenceList.push({
            id: curSentence.id,
        });
    }
    return res;
}

/**
 *
 * @param {Range} range
 */
function checkRangeInSymbol(base, range) {
    let curNode = range.commonAncestorContainer;
    while (curNode != base) {
        if (domHelper.hasCls(curNode, "seg-symbol")) return true;
        curNode = curNode.parentElement;
    }
    return false;
}

/**
 *
 * @param {Element} base
 * @param {Range} range
 */
function checkCursorInStartOrEnd(base, range) {
    let newRange = document.createRange();
    newRange.setStart(base, 0);
    newRange.setEnd(range.endContainer, range.endOffset);
    console.log("#-# split left", newRange.cloneContents().textContent);
    let rangeCopy = newRange.cloneContents();
    if (!rangeCopy.hasChildNodes() || rangeCopy.textContent.length == 0) return true;
    newRange.setStart(range.endContainer, range.endOffset);
    newRange.setEndAfter(base.lastChild);
    console.log("#-# split right", newRange.cloneContents().textContent);
    rangeCopy = newRange.cloneContents();
    if (!rangeCopy.hasChildNodes() || rangeCopy.textContent.length == 0) return true;
    newRange.detach();
    return false;
}

/**
 *
 * @param {DocumentFragment} fragment
 */
function getFramentInnerHtml(fragment) {
    let tempDiv = domHelper.div();
    while (domHelper.hasChild(fragment)) {
        const tempNode = fragment.firstChild;
        tempNode.remove();
        tempDiv.append(tempNode);
    }
    unshellTmx(tempDiv);
    return tempDiv.innerHTML;
}

/**
 *
 * @param {HTMLElement} tmx
 */
function removeTmXEl(tmx) {
    if (tmx.nodeName === TERM_MIDDLE_NODE_NAME_UPPER) {
        if (tmx.childNodes.length == 1 && isSegSymbol(tmx.childNodes[0])) {
            const segEl = tmx.childNodes[0];
            segEl.remove();
            tmx.parentNode.insertBefore(segEl, tmx);
            tmx.remove();
            return;
        }
    }

    const txt = tmx.innerText;
    if (!!tmx.previousSibling && isTextNode(tmx.previousSibling)) {
        tmx.previousSibling.textContent += txt;
    } else if (!!tmx.nextSibling && isTextNode(tmx.nextSibling)) {
        tmx.nextSibling.textContent = txt + tmx.nextSibling.textContent;
    } else {
        const newTextNode = document.createTextNode(txt);
        tmx.parentNode.insertBefore(newTextNode, tmx);
    }
    tmx.remove();
}

/**
 *
 * @param {HTMLElement} el
 */
function unshellTmx(el) {
    while (true) {
        let tmmEl = el.querySelector(TERM_MIDDLE_NODE_NAME);
        if (!tmmEl) break;
        removeTmXEl(tmmEl);
    }

    while (true) {
        let tmsEl = el.querySelector(TERM_START_NODE_NAME);
        if (!tmsEl) break;
        removeTmXEl(tmsEl);
    }

    while (true) {
        let tmeEl = el.querySelector(TERM_END_NODE_NAME);
        if (!tmeEl) break;
        removeTmXEl(tmeEl);
    }
}

/**
 *
 * @param {Element} base
 * @param {Range} range
 */
function getSplitedParts(base, range) {
    let newRange = document.createRange();
    newRange.setStart(base, 0);
    newRange.setEnd(range.endContainer, range.endOffset);
    const firstPart = getFramentInnerHtml(newRange.cloneContents());
    newRange.setStart(range.endContainer, range.endOffset);
    newRange.setEndAfter(base.lastChild);
    const secondPart = getFramentInnerHtml(newRange.cloneContents());
    newRange.detach();
    return [firstPart, secondPart];
}

/**
 *
 * @param {*} sentence
 * @param {*} projectId
 * @param {*} fileId
 * @param {*} pos
 * @returns
 */
function makeupSplitContext(sentence, projectId, fileId, splittedParts) {
    let res = {
        projectId,
        fileId,
        sentenceId: sentence.id,
        oldSrc: sentence.srcWithFormat,
        splittedParts,
    };

    return res;
}

/**
 *
 * @param {String} src
 */
function dealSrcForCopy(src) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = src;
    for (let i = 0; i < wrapper.childNodes.length; i++) {
        const styleId = domHelper.attr(wrapper.childNodes[i], "data-sty-id");
        !!styleId && domHelper.attr(wrapper.childNodes[i], "data-tag-name", `sty_${styleId}`);
    }
    return wrapper.innerHTML;
}

export {
    TU_STATUS,
    TU_STATUS_TEXTS,
    getStageDetailText,
    CAT_FLOW_TEXTS,
    CAT_AI_ASSISTANT_WARNING,
    TRANSLATE_READONLY_TU_STATUS,
    TRANSLATE_EDITABLE_TU_STATUS,
    TRANSLATE_CONFIRMABLE_TU_STATUS,
    TRANSLATE_CAT_DISABLED_TU_STATUS,
    REVIEW_READONLY_TU_STATUS,
    REVIEW_EDITABLE_TU_STATUS,
    REVIEW_CAT_DISABLED_TU_STATUS,
    PROOFREAD_READONLY_TU_STATUS,
    PROOFREAD_EDITABLE_TU_STATUS,
    PROOFREAD_CAT_DISABLED_TU_STATUS,
    EDITOR_MODE,
    TARGET_SOURCE,
    MATCH_SCORE_STATUS,
    PRETRANSLATE_BY,
    ASSET_SOURCE,
    ASSET_SOURCE_TEXT,
    FONT_SIZE_MAP,
    AssetSourceLoader,
    formatSrcWithTBList,
    SRC_CELL_CLASS_NAME,
    TGT_CELL_CLASS_NAME,
    getSectionText,
    generateSaveSentence,
    generateTranslateSentence,
    MAX_LENGTH_OF_SRC_TEXT,
    MAX_LENGTH_OF_TGT_TEXT,
    updateSentenceInfoWithResponse,
    QA_LEVEL,
    QA_LEVEL_TEXT,
    wrapperSentenceForQaCheck,
    QaSentenceMap,
    getQaCheckableSentences,
    updateSentenceQaInfo,
    SimpleActionSource,
    srcTextExtractor,
    tgtTextExtractor,
    generateSegmentRangeId,
    SENTENCE_LOCK_STATE,
    SENTENCE_UPDATING_STATE,
    CAT_WS_RESPONSE_CODE,
    CAT_WS_STATE,
    createVirtualTaskItemForPM,
    setEditFontSize,
    FontSizeScaler,
    makeupCombineContext,
    makeupSplitContext,
    checkRangeInSymbol,
    checkCursorInStartOrEnd,
    getSplitedParts,
    dealSrcForCopy,
    REPLACEMENT_SOURCE2TARGET_SOURCE,
};
